import { dateTimeFormatter, useKey } from '@core/logic';
import { Card, Fade, Stack, Typography, ButtonBase, IconButton } from '@mui/material';
import { useEffect, useRef } from 'react';
import { useSnapshot } from 'valtio';
import { useTheme } from '@mui/material/styles';
import { ArrowBoxOut } from '@deepup/icons';
import { useSMANumbers } from '@core/logic/hooks/useSMANumbers';
import { useParams } from 'react-router-dom';
import {
  openPhotoInNewTab,
  photosState,
  showNextPhoto,
  setPreviewPosition,
  setIsHoveringPreview,
  updatePhotoEverywhere,
} from './state';

export const PhotoPreview = () => {
  const {
    showPreview,
    previewPhotos,
    currentPhotoIndex,
    isLoading,
    previewPosition,
    hoverPreviewPosition,
    selectedPhoto,
  } = useSnapshot(photosState);
  const photoRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const currentPhoto = previewPhotos[currentPhotoIndex];
  const nextPhotoPressed = useKey(' ', true);
  const theme = useTheme();
  const { fetchSMANumbers } = useSMANumbers();
  const { projectId } = useParams();

  useEffect(() => {
    if (previewPhotos.length > 1 && nextPhotoPressed) {
      showNextPhoto();
    }
  }, [nextPhotoPressed, previewPhotos.length]);

  useEffect(() => {
    if (currentPhoto) {
      photoRefs.current.get(currentPhoto.id)?.scrollIntoView({
        behavior: 'smooth',
      });
    }
  }, [currentPhoto]);

  const photoWidth = theme.spacing(37);
  const maxWidth = theme.spacing(45);
  const maxWidthPx = Number.parseFloat(String(maxWidth));

  const targetPosition = hoverPreviewPosition || previewPosition;
  let positioningStyles = {};

  if (targetPosition) {
    const PREVIEW_WIDTH = Number.isFinite(maxWidthPx) ? maxWidthPx : 380;
    const PREVIEW_HEIGHT = 600;
    const OFFSET = 5;

    let left = targetPosition.x + OFFSET;
    let top = targetPosition.y + OFFSET;

    if (left + PREVIEW_WIDTH > window.innerWidth) {
      left = targetPosition.x - PREVIEW_WIDTH - OFFSET;
    }

    if (left < OFFSET) {
      left = OFFSET;
    }

    if (top + PREVIEW_HEIGHT > window.innerHeight) {
      top = window.innerHeight - PREVIEW_HEIGHT - OFFSET;
      // Ensure it doesn't go off-screen top
      if (top < OFFSET) top = OFFSET;
    }

    positioningStyles = { left, top };
  } else {
    positioningStyles = {
      right: theme.spacing(1),
      bottom: theme.spacing(1),
    };
  }

  const handleMouseEnter = () => {
    setIsHoveringPreview(true);
  };

  const handleMouseLeave = () => {
    setIsHoveringPreview(false);
  };

  useEffect(() => {
    const updateOnSelectedChange = async () => {
      const currentSelectedPhoto = photosState.selectedPhoto;
      if (!projectId || !currentSelectedPhoto) return;
      try {
        const smaNumbers = await fetchSMANumbers(projectId, currentSelectedPhoto.id, 'photos');
        const updated = {
          ...currentSelectedPhoto,
          orderNumbers: smaNumbers.map((sma) => ({
            type: sma.type,
            value: sma.value,
            subType: sma.subType,
          })),
        };
        updatePhotoEverywhere(updated);
      } catch (error) {
        console.error('Failed to update photo order numbers:', error);
      }
    };
    updateOnSelectedChange();
  }, [projectId, selectedPhoto?.id]);

  return (
    <Fade in={showPreview} timeout={300} onExited={() => setPreviewPosition(null)}>
      <Card
        elevation={12}
        sx={{
          position: 'fixed',
          zIndex: theme.zIndex.modal,
          ...positioningStyles,
        }}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {currentPhoto && (
          <ButtonBase
            onClick={() => openPhotoInNewTab(currentPhoto)} // Updated to open image in a new tab
            sx={{
              width: '100%',
              padding: theme.spacing(1),
              color: 'white',
            }}
          >
            <IconButton>
              <ArrowBoxOut />
            </IconButton>
            <Typography variant="body2">Open in new tab</Typography>
          </ButtonBase>
        )}
        <Stack
          direction="row"
          overflow="hidden"
          maxWidth={maxWidth}
          sx={{ scrollSnapType: 'x mandatory' }}
        >
          {!isLoading &&
            previewPhotos.map((p, i) => (
              <Stack
                key={p.id}
                sx={{
                  scrollSnapAlign: i < previewPhotos.length - 1 ? 'start' : 'end',
                  width: photoWidth,
                  transition: theme.transitions.create('opacity', { easing: 'ease-in-out' }),
                  opacity: i === currentPhotoIndex ? 1 : 0.3,
                  '& img': {
                    minHeight: 400,
                  },
                  pb: 1,
                }}
                ref={(ref) => {
                  if (ref) {
                    photoRefs.current.set(p.id, ref);
                  } else {
                    photoRefs.current.delete(p.id);
                  }
                }}
                gap={1}
              >
                <img
                  src={p.url}
                  alt="preview"
                  width={photoWidth}
                  onClick={previewPhotos.length > 0 ? undefined : () => openPhotoInNewTab(p)}
                  onError={() => console.error('Failed to load image.')}
                />
                <Typography variant="body2" color="textSecondary" sx={{ px: 1 }}>
                  {dateTimeFormatter(p.recordedAt)}
                </Typography>
                <Typography
                  variant="caption"
                  color="textSecondary"
                  sx={{ px: 1, display: 'block' }}
                >
                  ID: {p.id}
                </Typography>
                {p.comment && (
                  <Typography variant="body2" color="textSecondary" sx={{ px: 1 }}>
                    Comment: {p.comment}
                  </Typography>
                )}
              </Stack>
            ))}
        </Stack>
      </Card>
    </Fade>
  );
};
